// src/i18n/Langue.jsx
// ─────────────────────────────────────────────────────────────
// Langue du site : français ou anglais.
//
// Chaque page range ses textes dans un fichier voisin, « <Page>.textes.js »,
// qui exporte { fr: {...}, en: {...} } avec exactement les mêmes clés.
// Dans la page :
//     const t = useTextes(textes);
//     <h1>{t.titre}</h1>            // texte simple
//     <p>{t.nbDevis(3)}</p>         // texte avec une valeur : une fonction
//
// Ce qui est une DONNÉE ne se traduit pas ici : statuts enregistrés en
// base (« validé », « en cours »), noms de plats, références de devis.
// Seul leur affichage peut passer par un libellé.
//
// Le choix du visiteur est mémorisé sur son appareil. À la première
// visite, on suit la langue du navigateur : français si elle l'est,
// anglais sinon.
// ─────────────────────────────────────────────────────────────
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const LANGUES = [
  { code: 'fr', court: 'FR', nom: 'Français' },
  { code: 'en', court: 'EN', nom: 'English' },
];

const CLE_MEMOIRE = 'langue';
const LOCALES = { fr: 'fr-FR', en: 'en-GB' };

function langueInitiale() {
  try {
    const choix = localStorage.getItem(CLE_MEMOIRE);
    if (LOCALES[choix]) return choix;
  } catch (e) { /* stockage indisponible : on suit le navigateur */ }
  const navigateur = (navigator.languages && navigator.languages[0]) || navigator.language || 'fr';
  return navigateur.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

const Contexte = createContext({ langue: 'fr', locale: LOCALES.fr, setLangue: () => {} });

export function LangueProvider({ children }) {
  const [langue, setLangueEtat] = useState(langueInitiale);

  const setLangue = useCallback((code) => {
    if (!LOCALES[code]) return;
    setLangueEtat(code);
    try { localStorage.setItem(CLE_MEMOIRE, code); } catch (e) { /* choix non mémorisé */ }
  }, []);

  // Lecteurs d'écran et traduction automatique du navigateur s'appuient sur cet attribut
  useEffect(() => { document.documentElement.lang = langue; }, [langue]);

  const valeur = useMemo(() => ({ langue, locale: LOCALES[langue], setLangue }), [langue, setLangue]);
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

/* { langue: 'fr' | 'en', locale: 'fr-FR' | 'en-GB', setLangue } — `locale` sert aux dates et aux nombres */
export const useLangue = () => useContext(Contexte);

/* Textes d'une page dans la langue courante */
export function useTextes(textes) {
  const { langue } = useLangue();
  return textes[langue] || textes.fr;
}
